import { ClassicPreset } from 'rete'
import {
  AttributeManagementNextStepTypeEnum,
  AttributeManagementTypeEnum,
  type PipelineStep,
  type PipelineStepNextStepsRule,
  PipelineStepType,
} from '../../../../../types/attributeManagement.types.ts'
import { Instacart_Catalog_Catflow_V1_StepCategory } from '../../../../../api'
import { type PipelineEditorStepNodeInput } from './types.ts'

/**
 * Interface for the Node
 */
export interface PipelineEditorStepNodeInterface
  extends ClassicPreset.Node<
    { [label: string]: ClassicPreset.Socket }, // Input sockets
    { [label: string]: ClassicPreset.Socket }, // Output sockets
    {
      [key in string]: ClassicPreset.Control
    }
  > {
  label: string
  input: PipelineEditorStepNodeInput
  width: number
  height: number
}

/**
 * Definition of a Pipeline Step Node
 */
export class PipelineEditorStepNode
  extends ClassicPreset.Node<
    { [label: string]: ClassicPreset.Socket }, // Input sockets
    { [label: string]: ClassicPreset.Socket }, // Output sockets
    {
      [key in string]: ClassicPreset.Control
    }
  >
  implements PipelineEditorStepNodeInterface
{
  width = 180
  height = 120
  input: PipelineEditorStepNodeInput
  x: number = 0
  y: number = 0

  constructor(label: string, input: PipelineEditorStepNodeInput) {
    super(label)
    this.input = input
  }
}

/**
 * Helpers
 */

export const buildNodeSockets = (
  step: PipelineStep,
  node: PipelineEditorStepNode,
  socket: ClassicPreset.Socket
) => {
  const stepType = step?.type ?? AttributeManagementTypeEnum.Control
  const isControl = stepType.toLowerCase() === PipelineStepType.Control.toLowerCase()
  const stepCategory =
    node?.input?.stepVersion?.step?.stepCategory ??
    Instacart_Catalog_Catflow_V1_StepCategory.Unknown
  let numOfInputs = 0
  let numOfOutputs = 0
  const outputLabels: string[] = []
  const inputLabels: string[] = []

  if (!isControl) {
    numOfInputs = stepCategory === Instacart_Catalog_Catflow_V1_StepCategory.Input ? 0 : 1
    numOfOutputs = 1
  } else if (isControl) {
    // TODO: Whenever we add a new control type we should modify this area to handle any additional business logic
    const controlType =
      step?.nextStepsRule?.type ?? AttributeManagementNextStepTypeEnum.SingleNextStep
    if (controlType === AttributeManagementNextStepTypeEnum.Fork) {
      numOfInputs = 1
      numOfOutputs = (step?.nextStepsRule?.possibleNextSteps ?? []).length
    } else if (controlType === AttributeManagementNextStepTypeEnum.RegexRules) {
      const matches = step?.nextStepsRule?.matches ?? []
      numOfInputs = 1
      numOfOutputs = matches.length + 1
      for (const match of matches) {
        outputLabels.push(match.regex)
      }
      outputLabels.push('None')
    } else if (controlType === AttributeManagementNextStepTypeEnum.NumericRange) {
      const ranges = step?.nextStepsRule?.ranges ?? []
      numOfInputs = 1
      numOfOutputs = ranges.length + 1
      for (const range of ranges) {
        outputLabels.push(`${range.rangeLow}-${range.rangeHigh}`)
      }
      outputLabels.push('Default')
    } else if (controlType === AttributeManagementNextStepTypeEnum.FieldMatch) {
      numOfInputs = 1
      numOfOutputs = 2
      outputLabels.push('Match')
      outputLabels.push('Miss')
    } else if (controlType === AttributeManagementNextStepTypeEnum.Halt) {
      numOfInputs = 1
      numOfOutputs = 0
    }
  }

  for (let index = 0; index < numOfInputs; index++) {
    const id: string = `input_${index}`
    node.addInput(id, new ClassicPreset.Input(socket, inputLabels?.[index] ?? '', true))
  }

  for (let index = 0; index < numOfOutputs; index++) {
    const id: string = `output_${index}`
    node.addOutput(id, new ClassicPreset.Output(socket, outputLabels?.[index] ?? ''))
  }
}

export const handleNodeConnectionRemoved = (
  sourceNode: PipelineEditorStepNode,
  sourceIndex: number,
  targetNode: PipelineEditorStepNode,
  targetIndex: number
): PipelineStep => {
  const sourceNextStepsRule = sourceNode?.input?.step?.nextStepsRule
  const sourceNextStepRuleType =
    sourceNextStepsRule?.type ?? AttributeManagementNextStepTypeEnum.SingleNextStep
  const sourcePossibleNextSteps = sourceNode?.input?.step?.nextStepsRule?.possibleNextSteps ?? []

  if (sourceNextStepRuleType === AttributeManagementNextStepTypeEnum.SingleNextStep) {
    const updatedPipelineNextSteps = sourcePossibleNextSteps.filter(
      pipelineStepId => targetNode.id !== pipelineStepId
    )
    if (updatedPipelineNextSteps.length === 0) {
      return {
        ...sourceNode.input.step,
        nextStepsRule: null,
      }
    }

    const existingNextStep: string | undefined = sourceNode?.input?.step?.nextStepsRule?.nextStep
    // Replace with the first step.
    const updatedNextStep =
      existingNextStep === targetNode.id ? updatedPipelineNextSteps[0] : existingNextStep

    return {
      ...sourceNode.input.step,
      nextStepsRule: {
        type: AttributeManagementNextStepTypeEnum.SingleNextStep,
        nextStep: updatedNextStep,
        possibleNextSteps: updatedPipelineNextSteps,
      },
    }
  }
  if (!sourceNextStepsRule) return sourceNode.input.step

  const nextStepsRule: PipelineStepNextStepsRule = { ...sourceNextStepsRule }
  nextStepsRule.possibleNextSteps = [...nextStepsRule.possibleNextSteps]
  nextStepsRule.possibleNextSteps[sourceIndex] = ''

  // TODO: Whenever we add a new control type we should modify this area to handle any additional business logic
  if (sourceNextStepRuleType === AttributeManagementNextStepTypeEnum.Fork) {
    nextStepsRule.nextPipelineSteps = [...sourcePossibleNextSteps]
  } else if (sourceNextStepRuleType === AttributeManagementNextStepTypeEnum.RegexRules) {
    // Check if we are not removing `noMatchNextPipelineStep`. No Match is the last index.
    if (nextStepsRule.matches && sourceIndex < nextStepsRule.matches.length) {
      nextStepsRule.matches[sourceIndex].nextPipelineStep = ''
    } else {
      nextStepsRule.noMatchNextPipelineStep = undefined
      nextStepsRule.possibleNextSteps = nextStepsRule.possibleNextSteps.splice(sourceIndex - 1, 1)
    }
  } else if (sourceNextStepRuleType === AttributeManagementNextStepTypeEnum.NumericRange) {
    if (nextStepsRule.ranges && sourceIndex < nextStepsRule.ranges.length) {
      nextStepsRule.ranges[sourceIndex].nextPipelineStep = ''
    } else {
      nextStepsRule.defaultNextPipelineStep = undefined
      nextStepsRule.possibleNextSteps = nextStepsRule.possibleNextSteps.splice(sourceIndex - 1, 1)
    }
  } else if (sourceNextStepRuleType === AttributeManagementNextStepTypeEnum.FieldMatch) {
    if (sourceIndex == 0) {
      nextStepsRule.nextPipelineStepOnMatchHit = ''
    } else {
      nextStepsRule.nextPipelineStepOnMatchMiss = ''
    }
  }

  return {
    ...sourceNode.input.step,
    nextStepsRule: nextStepsRule,
  }
}

export const handleNodeConnectionAdded = (
  sourceNode: PipelineEditorStepNode,
  sourceIndex: number,
  targetNode: PipelineEditorStepNode,
  targetIndex: number
): PipelineStep => {
  const stepType = sourceNode?.input?.step?.type ?? AttributeManagementTypeEnum.Control
  const isControl = stepType.toLowerCase() === PipelineStepType.Control.toLowerCase()

  // If this exists we can conclude that this is a control node.
  const existingNextStepRuleType = sourceNode?.input?.step?.nextStepsRule?.type
  const existingPossibleNextSteps = [
    ...(sourceNode?.input?.step?.nextStepsRule?.possibleNextSteps ?? []),
  ]
  existingPossibleNextSteps[sourceIndex] = targetNode.id
  console.log(existingPossibleNextSteps, sourceIndex, targetNode, existingNextStepRuleType)

  const nextStepsRule: PipelineStepNextStepsRule = {
    type: isControl ? existingNextStepRuleType : AttributeManagementNextStepTypeEnum.SingleNextStep,
    ...(sourceNode?.input?.step?.nextStepsRule ?? {}),
    nextStep: targetNode.id,
    possibleNextSteps: existingPossibleNextSteps,
  }

  // TODO: Whenever we add a new control type we should modify this area to handle any additional business logic
  if (existingNextStepRuleType === AttributeManagementNextStepTypeEnum.Fork) {
    nextStepsRule.nextPipelineSteps = [...existingPossibleNextSteps]
  } else if (existingNextStepRuleType === AttributeManagementNextStepTypeEnum.RegexRules) {
    if (nextStepsRule.matches && sourceIndex < nextStepsRule.matches.length) {
      nextStepsRule.matches[sourceIndex].nextPipelineStep = targetNode.id
    } else {
      nextStepsRule.noMatchNextPipelineStep = targetNode.id
    }
  } else if (existingNextStepRuleType === AttributeManagementNextStepTypeEnum.NumericRange) {
    if (nextStepsRule.ranges && sourceIndex < nextStepsRule.ranges.length) {
      nextStepsRule.ranges = [...nextStepsRule.ranges]
      nextStepsRule.ranges[sourceIndex].nextPipelineStep = targetNode.id
    } else {
      nextStepsRule.defaultNextPipelineStep = targetNode.id
    }
  } else if (existingNextStepRuleType === AttributeManagementNextStepTypeEnum.FieldMatch) {
    if (sourceIndex == 0) {
      nextStepsRule.nextPipelineStepOnMatchHit = targetNode.id
    } else {
      nextStepsRule.nextPipelineStepOnMatchMiss = targetNode.id
    }
  }

  return {
    ...sourceNode.input.step,
    nextStepsRule: nextStepsRule,
  }
}
